import { OrganizationState, ActivityState, ToastActions, DialogActions } from '@app/_store';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { IdTokenClaims } from '@azure/msal-common';
import { Injectable } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AccountInfo, AuthenticationResult, EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { filter, tap, catchError, take, finalize, switchMap, map, concatMap } from 'rxjs/operators';
import { AccountActions } from './account.actions';
import { Account, AccountStateModel, PatchUserResponse } from './account.models';
import { b2cPolicies, msalConfig } from '@app/auth-config';
import { environment } from '@environments/environment';
import { Observable, of, Subject } from 'rxjs';
import { patch } from '@ngxs/store/operators';
import { Navigate } from '@ngxs/router-plugin';
import { CookieService } from 'ngx-cookie-service';

type IdTokenClaimsWithPolicyId = IdTokenClaims & {
  acr?: string;
  tfp?: string;
};

@State<AccountStateModel>({
  name: 'account',
  defaults: {
    account: undefined,
    showHiddenAdminFeatures: false,
  },
})
@Injectable()
export class AccountState {
  constructor(
    private store: Store,
    private http: HttpClient,
    private msalBroadcastService: MsalBroadcastService,
    private msalService: MsalService,
    private cookieService: CookieService
  ) {
    // LOGOUT_SUCCESS is broadcast to other tabs but also fires in the tab that is logging out itself;
    // only follow it here when the logout happened elsewhere, so our own logoutRedirect to B2C isn't cut short
    this.msalBroadcastService.msalSubject$
      .pipe(filter((msg: EventMessage) => msg.eventType === EventType.LOGOUT_SUCCESS && !this.loggingOut))
      .subscribe(() => {
        console.log('User logged out from other tab or window');
        window.location.href = msalConfig.auth.postLogoutRedirectUri;
      });

    this.msalBroadcastService.inProgress$
      .pipe(filter((status: InteractionStatus) => status === InteractionStatus.None))
      .subscribe(() => {
        if (this.checkAndSetActiveAccount()) {
          this.store.dispatch(new AccountActions.Initialize());
        }
      });

    this.msalBroadcastService.msalSubject$.subscribe((result: EventMessage) => {
      if (result.eventType === EventType.ACQUIRE_TOKEN_FAILURE) {
        this.cookieService.delete('link-account');
      }

      // Since msal-browser 5 the LOGIN_SUCCESS payload is the AccountInfo itself; ACQUIRE_TOKEN_SUCCESS still carries an AuthenticationResult
      if (result.eventType === EventType.LOGIN_SUCCESS || result.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) {
        const account =
          result.eventType === EventType.LOGIN_SUCCESS
            ? (result.payload as AccountInfo)
            : (result.payload as AuthenticationResult).account;
        const idtoken = account?.idTokenClaims as IdTokenClaimsWithPolicyId;

        if (idtoken?.acr === b2cPolicies.names.signUpSignIn || idtoken?.tfp === b2cPolicies.names.signUpSignIn) {
          this.msalService.instance.setActiveAccount(account);
        }
      }

      return result;
    });
  }

  @Selector()
  static account(state: AccountStateModel): Account | undefined {
    return state?.account;
  }

  @Selector()
  static showHiddenAdminFeatures(state: AccountStateModel): boolean {
    return state.showHiddenAdminFeatures;
  }

  private initInProgress$ = new Subject();
  private actionInProgress = false;
  private loggingOut = false;

  @Action(AccountActions.Initialize)
  initialize(ctx: StateContext<AccountStateModel>, action: AccountActions.Initialize): Observable<void> {
    if (this.actionInProgress) {
      return this.initInProgress$.pipe(
        take(1),
        switchMap(() => ctx.dispatch(action))
      );
    }

    this.actionInProgress = true;

    const currentOrganization = this.store.selectSnapshot(OrganizationState.current);
    const currentActivity = this.store.selectSnapshot(ActivityState.currentActivity);

    const organizationId = action.organizationId ?? currentOrganization?.id;
    const activityId = action.activityId ?? currentActivity?.id;
    console.log(`Getting token for ${organizationId} - ${activityId} `);

    const getAccountRequest = {
      initialLogin: ctx.getState().account === undefined,
      organizationId: organizationId,
      activityId: activityId,
    };

    const linkTo = this.cookieService.get('link-account');
    this.cookieService.delete('link-account');
    if (linkTo && linkTo.length > 0) {
      getAccountRequest['linkToAccount'] = JSON.parse(linkTo);
      getAccountRequest.initialLogin = true;
    }

    return this.http.post<Account>(`${environment.apiUrl}/users/account`, getAccountRequest).pipe(
      map(response => {
        if (response.identityProvider && this.cookieService.get('preferred-idp').length === 0) {
          this.cookieService.set('preferred-idp', response.identityProvider, 30, '/');
        }

        const jwtToken = JSON.parse(atob(response.token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
        const expires = new Date(jwtToken.exp * 1000);
        const timeout = expires.getTime() - Date.now() - 60 * 1000;

        console.log(`scheduling refreh in ${timeout / 1000} seconds`);
        this.startAuthenticateTimer(ctx, timeout);

        ctx.patchState({
          account: response,
        });
        return response;
      }),
      concatMap(response => {
        if (!response.email || response.email.length === 0) {
          ctx.dispatch(
            new DialogActions.Confirm(
              'E-post saknas',
              'Ditt konto hos Klubbavgift saknar e-postadress vilket behövs för att vi ska kunna kommunicera händelser runt din organisation och dina aktiviteter. Vänligen ange en e-postadress i dina inställningar.',
              () => of(null),
              false,
              'Okej!'
            )
          );
          ctx.dispatch(new Navigate(['/profile']));
        }

        return of(null);
      }),
      catchError((err: HttpErrorResponse) => {
        if (err.status == 403) {
          window.location.href = '/';
        } else if (err.status == 401) {
          console.log('Got a 401! Clearing cache and sending action again');
          this.msalService.instance.clearCache();
          console.log('cache cleared, sending ' + JSON.stringify(action));
          ctx.dispatch(action);
          console.log('action sent!');
        }
        throw err;
      }),
      finalize(() => {
        this.actionInProgress = false;
        this.initInProgress$.next(null); // Notify waiting actions
      })
    );
  }

  @Action(AccountActions.ToggleHiddenAdminFeatures)
  toggleHiddenAdminFeatures(ctx: StateContext<AccountStateModel>) {
    const showHiddenAdminFeatures = ctx.getState().showHiddenAdminFeatures;

    ctx.setState(
      patch<AccountStateModel>({
        showHiddenAdminFeatures: !showHiddenAdminFeatures,
      })
    );
  }

  @Action(AccountActions.UpdateAccount)
  updateAccount(ctx: StateContext<AccountStateModel>, action: AccountActions.UpdateAccount) {
    const currentAccount = ctx.getState().account;

    return this.http.patch<PatchUserResponse>(`${environment.apiUrl}/users/${currentAccount.id}`, action.operations).pipe(
      tap((response: PatchUserResponse) => {
        ctx.setState(
          patch<AccountStateModel>({
            account: patch<Account>({
              settings: response.settings,
              email: response.email,
              originalEmail: response.originalEmail,
              phone: response.phone,
            }),
          })
        );
        ctx.dispatch(new ToastActions.ShowSuccess('Dina inställningar har ändrats.'));
      })
    );
  }

  authenticateTimeout: number;
  private startAuthenticateTimer(ctx: StateContext<AccountStateModel>, timeout: number): void {
    this.stopAuthenticateTimer();

    this.authenticateTimeout = window.setTimeout(() => {
      try {
        console.log('refreshing due to token expiration');

        ctx.dispatch(new AccountActions.Initialize());
      } catch (e) {
        console.log('Unable to renew token in timer');
        console.log(e);
        console.log('WHAT TO DO HERE!!!');
      }
    }, timeout);
  }

  private stopAuthenticateTimer(): void {
    if (this.authenticateTimeout) {
      clearTimeout(this.authenticateTimeout);
    }
  }

  @Action(AccountActions.Logout)
  logout(): void {
    this.stopAuthenticateTimer();
    this.loggingOut = true;
    const logoutRequest = {
      account: this.msalService.instance.getActiveAccount(),
    };

    this.msalService.logoutRedirect(logoutRequest);
  }

  checkAndSetActiveAccount(): boolean {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    const activeAccount = this.msalService.instance.getActiveAccount();
    if (activeAccount) return true;

    if (this.msalService.instance.getAllAccounts().length > 0) {
      const accounts = this.msalService.instance.getAllAccounts();
      // add your code for handling multiple accounts here
      this.msalService.instance.setActiveAccount(accounts[0]);

      return true;
    }

    return false;
  }
}
